import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Empty, Form, Input, Modal, Popconfirm, Select,
  Space, Table, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import {
  BellOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  createDocumentType, deleteDocument, deleteDocumentType, getDocumentTypes,
  getDocuments, getParticipants, notifyDocument, updateDocument, updateDocumentType, uploadDocument,
} from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';

const { Text } = Typography;

const StageDocumentsTab = ({ stage }) => {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [typeModal, setTypeModal] = useState({ open: false, record: null });
  const [editModal, setEditModal] = useState({ open: false, record: null });
  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: t }, { data: p }] = await Promise.all([
        getDocuments(stage.id),
        getDocumentTypes(),
        getParticipants(stage.id, { per_page: 100 }),
      ]);
      setDocs(d.data || []);
      setTypes(t.data || []);
      setParticipants(p.data || []);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Belgeler yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [stage.id]);

  useEffect(() => { load(); }, [load]);

  const submitUpload = async () => {
    try {
      const v = await form.validateFields();
      if (!file) return message.warning('Lütfen bir dosya seçin.');

      const fd = new FormData();
      fd.append('file', file);
      if (v.title) fd.append('title', v.title);
      if (v.competition_document_type_id) fd.append('competition_document_type_id', v.competition_document_type_id);
      if (v.competition_participant_id) fd.append('competition_participant_id', v.competition_participant_id);
      // FormData tum degerleri string'e cevirir; PHP'nin boolean validasyonu
      // "false" string'ini true sayardi, o yuzden 1/0 gonderiliyor.
      fd.append('visible_to_parent', v.visible_to_parent ? '1' : '0');
      if (v.notes) fd.append('notes', v.notes);

      setSaving(true);
      await uploadDocument(stage.id, fd);
      message.success('Belge yüklendi.');
      setUploadOpen(false);
      setFile(null);
      form.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    try {
      const v = await editForm.validateFields();
      setEditSaving(true);
      await updateDocument(editModal.record.id, {
        title: v.title || null,
        competition_document_type_id: v.competition_document_type_id ?? null,
        competition_participant_id: v.competition_participant_id ?? null,
        visible_to_parent: !!v.visible_to_parent,
        notes: v.notes || null,
      });
      message.success('Belge güncellendi.');
      setEditModal({ open: false, record: null });
      editForm.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e, 'Belge güncellenemedi.'));
    } finally {
      setEditSaving(false);
    }
  };

  const submitType = async () => {
    try {
      const v = await typeForm.validateFields();
      if (typeModal.record) await updateDocumentType(typeModal.record.id, v);
      else await createDocumentType(v);
      message.success('Kaydedildi.');
      setTypeModal({ open: false, record: null });
      typeForm.resetFields();
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrorMessage(e));
    }
  };

  const columns = [
    {
      title: 'Belge',
      render: (_, r) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{r.title || r.original_name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.original_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Tip',
      dataIndex: ['type', 'label'],
      width: 150,
      render: (v, r) => (v ? <Tag color={r.type?.color || 'default'}>{v}</Tag> : '—'),
    },
    {
      title: 'Kapsam',
      width: 220,
      render: (_, r) => {
        if (!r.competition_participant_id) return <Tag>Etap geneli</Tag>;
        const s = r.participant?.student;
        return <Tag color="blue">{s ? `${s.name} ${s.surname}` : 'Kişiye özel'}</Tag>;
      },
    },
    {
      title: 'Veli Görebilir',
      dataIndex: 'visible_to_parent',
      width: 130,
      render: (v) => (v ? <Tag color="success">Evet</Tag> : <Tag color="default">Hayır (kuruma özel)</Tag>),
    },
    {
      title: 'Yükleyen',
      width: 150,
      render: (_, r) => (
        <Space orientation="vertical" size={0}>
          <Text>{r.uploader ? `${r.uploader.name} ${r.uploader.surname}` : '—'}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(r.created_at).format('DD.MM.YYYY')}</Text>
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      width: 220,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Tooltip title="Görüntüle">
            <Button size="small" icon={<EyeOutlined />} onClick={() => window.open(r.stream_url, '_blank')} />
          </Tooltip>
          <Tooltip title="Düzenle">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditModal({ open: true, record: r });
                editForm.setFieldsValue({
                  title: r.title,
                  competition_document_type_id: r.competition_document_type_id,
                  competition_participant_id: r.competition_participant_id,
                  visible_to_parent: r.visible_to_parent,
                  notes: r.notes,
                });
              }}
            />
          </Tooltip>
          <Tooltip title="İndir">
            <Button size="small" icon={<DownloadOutlined />} onClick={() => window.open(r.download_url, '_blank')} />
          </Tooltip>
          <Tooltip title={r.visible_to_parent ? 'Velilere bildir' : 'Veliye kapalı belge — bildirilemez'}>
            <Button
              size="small"
              icon={<BellOutlined />}
              disabled={!r.visible_to_parent}
              onClick={async () => {
                try {
                  const { data } = await notifyDocument(r.id);
                  message.success(data.message);
                } catch (e) { message.warning(apiErrorMessage(e)); }
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Belge silinsin mi?"
            onConfirm={async () => {
              try {
                await deleteDocument(r.id);
                message.success('Silindi.');
                load();
              } catch (e) { message.error(apiErrorMessage(e)); }
            }}
            okText="Sil"
            cancelText="Vazgeç"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Belgeyi siz yüklersiniz, veli indirir"
        description='Kişiye özel yüklenen bir belge (örn. isme yazılmış davet mektubu) yalnızca o velinin ekranında görünür. "Veli görebilir" kapalıysa belge tamamen kuruma özeldir.'
      />

      <Space wrap style={{ marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => { setUploadOpen(true); form.setFieldsValue({ visible_to_parent: true }); }}
        >
          Belge Yükle
        </Button>
        <Button
          icon={<PlusOutlined />}
          onClick={() => { setTypeModal({ open: true, record: null }); typeForm.setFieldsValue({ is_active: true }); }}
        >
          Belge Tipleri
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={docs}
        size="small"
        scroll={{ x: 1000 }}
        pagination={false}
        locale={{ emptyText: <Empty description="Henüz belge yüklenmemiş." /> }}
      />

      <Modal
        title="Belge Yükle"
        open={uploadOpen}
        onOk={submitUpload}
        confirmLoading={saving}
        onCancel={() => { setUploadOpen(false); setFile(null); form.resetFields(); }}
        okText="Yükle"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Dosya" required>
            <Upload.Dragger
              beforeUpload={(f) => { setFile(f); return false; }}
              onRemove={() => setFile(null)}
              fileList={file ? [file] : []}
              maxCount={1}
              accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
            >
              <p className="ant-upload-drag-icon"><UploadOutlined /></p>
              <p className="ant-upload-text">Dosyayı sürükleyin veya tıklayın</p>
              <p className="ant-upload-hint">JPG, PNG, WEBP, PDF, Word — en fazla 30MB</p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item name="title" label="Başlık">
            <Input placeholder="Davet Mektubu / Otel Konfirmasyonu" />
          </Form.Item>
          <Form.Item name="competition_document_type_id" label="Belge Tipi">
            <Select
              allowClear
              placeholder="Tip seçin"
              options={types.map((t) => ({ value: t.id, label: t.label }))}
            />
          </Form.Item>
          <Form.Item
            name="competition_participant_id"
            label="Kişiye Özel mi"
            extra="Boş bırakırsanız etaptaki tüm veliler indirebilir."
          >
            <Select
              allowClear
              placeholder="Etap geneli"
              options={participants.map((p) => ({
                value: p.id,
                label: `${p.student?.name || ''} ${p.student?.surname || ''}`.trim(),
              }))}
            />
          </Form.Item>
          <Form.Item name="visible_to_parent" valuePropName="checked">
            <Checkbox>Veli görebilsin</Checkbox>
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Belgeyi Düzenle"
        open={editModal.open}
        onOk={submitEdit}
        confirmLoading={editSaving}
        onCancel={() => { setEditModal({ open: false, record: null }); editForm.resetFields(); }}
        okText="Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={editModal.record?.original_name}
          description="Dosyanın kendisi değişmez, sadece bilgileri güncellenir."
        />
        <Form form={editForm} layout="vertical">
          <Form.Item name="title" label="Başlık">
            <Input placeholder="Davet Mektubu / Otel Konfirmasyonu" />
          </Form.Item>
          <Form.Item name="competition_document_type_id" label="Belge Tipi">
            <Select
              allowClear
              placeholder="Tip seçin"
              options={types.map((t) => ({ value: t.id, label: t.label }))}
            />
          </Form.Item>
          <Form.Item
            name="competition_participant_id"
            label="Kişiye Özel mi"
            extra="Boş bırakırsanız etaptaki tüm veliler indirebilir."
          >
            <Select
              allowClear
              placeholder="Etap geneli"
              options={participants.map((p) => ({
                value: p.id,
                label: `${p.student?.name || ''} ${p.student?.surname || ''}`.trim(),
              }))}
            />
          </Form.Item>
          <Form.Item name="visible_to_parent" valuePropName="checked">
            <Checkbox>Veli görebilsin</Checkbox>
          </Form.Item>
          <Form.Item name="notes" label="Not">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Belge Tipleri"
        open={typeModal.open}
        onCancel={() => { setTypeModal({ open: false, record: null }); typeForm.resetFields(); }}
        footer={null}
        destroyOnHidden
      >
        <Card size="small" style={{ marginBottom: 12 }}>
          <Form form={typeForm} layout="inline" onFinish={submitType}>
            <Form.Item name="label" rules={[{ required: true, message: 'Ad girin.' }]}>
              <Input placeholder="Davet Mektubu, Muvafakatname…" style={{ width: 220 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                {typeModal.record ? 'Güncelle' : 'Ekle'}
              </Button>
            </Form.Item>
          </Form>
        </Card>
        <Table
          rowKey="id"
          size="small"
          dataSource={types}
          pagination={false}
          locale={{ emptyText: 'Tip tanımlanmamış.' }}
          columns={[
            { title: 'Tip', dataIndex: 'label' },
            {
              title: '',
              width: 80,
              render: (_, r) => (
                <Popconfirm
                  title="Tip silinsin mi?"
                  onConfirm={async () => {
                    try {
                      await deleteDocumentType(r.id);
                      message.success('Silindi.');
                      load();
                    } catch (e) { message.warning(apiErrorMessage(e)); }
                  }}
                  okText="Sil"
                  cancelText="Vazgeç"
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default StageDocumentsTab;
